import { useState } from "react";
import { usePrepPack, useTickPrep, type PackLine, type PrepPack } from "../../api/crew";
import { formatDateTime } from "../../api/dates";

/** The inspector's preparation pack -- WHAT TO TAKE: what to bring, what to
 * ask, what the QCP already established, and the parts to have on the van.
 * Read in the yard and ticked in the chamber, so it is cached as fetched and
 * every tick is applied at once and sent when it can be. */
export default function PrepPackView({ siteId }: { siteId: number }) {
  const pack = usePrepPack(siteId);
  if (pack.isPending) {
    return <section className="crew-card crew-state">Loading the preparation pack…</section>;
  }
  if (pack.isError) {
    return (
      <section className="crew-card">
        <p className="notice bad">The preparation pack could not be loaded: {pack.error.message}</p>
      </section>
    );
  }
  return <PackView pack={pack.data} siteId={siteId} />;
}

function PartsToTake({ plan }: { plan: PrepPack["plan"] }) {
  if (plan.suggestions.length === 0 && plan.question === null && plan.unavailable === null) {
    return null;
  }
  return (
    <details className="prep-parts" open={plan.suggestions.length > 0}>
      <summary>
        Parts to take{plan.supplier ? ` · ${plan.supplier.name}` : ""}
      </summary>
      {plan.question ? <p className="notice">{plan.question}</p> : null}
      {plan.unavailable ? <p className="crew-note">{plan.unavailable}</p> : null}
      {plan.suggestions.length > 0 ? (
        <ul className="prep-part-list">
          {plan.suggestions.map((s) => (
            <li key={`${s.part_id}-${s.variant}`} className={s.certain ? undefined : "uncertain"}>
              <span>
                {s.description} {s.size}
                {s.item_code ? <span className="crew-note"> · {s.item_code}</span> : null}
              </span>
              <span className="crew-note">
                {s.role} · {s.variant}
                {s.certain ? "" : " (candidate)"}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </details>
  );
}

export function PackView({ pack, siteId }: { pack: PrepPack; siteId: number }) {
  const tick = useTickPrep(siteId);
  const [queued, setQueued] = useState(0);
  const total = pack.sections.reduce((n, s) => n + s.lines.length, 0);
  const done = pack.sections.reduce((n, s) => n + s.lines.filter((l) => l.done).length, 0);
  return (
    <section className="crew-card prep-pack" aria-label="Preparation pack">
      <div className="prep-head">
        <h2 className="section-label">What to take</h2>
        <span className="crew-note">
          Preparation pack · {done} of {total} ticked
        </span>
        <a className="go-link prep-pdf" href={pack.pdf_url}>
          PDF
        </a>
      </div>
      {queued > 0 ? (
        <p className="notice warn">
          {queued} tick{queued === 1 ? "" : "s"} saved on the phone, waiting for signal.
        </p>
      ) : null}
      {tick.isError ? <p className="notice bad">{tick.error.message}</p> : null}
      {pack.sections.map((section) => (
        <div key={section.section} className="prep-section">
          {pack.sections.length > 1 ? (
            <h3 className="prep-section-title">
              {section.title}{" "}
              <span className="crew-note">
                {section.lines.filter((l) => l.done).length}/{section.lines.length}
              </span>
            </h3>
          ) : null}
          {section.notice ? <p className="notice">{section.notice}</p> : null}
          <ul className="prep-lines">
            {section.lines.map((line) => (
              <PrepLine
                key={line.key}
                line={line}
                askAnswer={section.section === "checks"}
                onTick={(next) =>
                  tick.mutate(
                    { item_key: line.key, done: next.done, answer: next.answer },
                    { onSuccess: (r) => setQueued((q) => (r.queued ? q + 1 : q)) },
                  )
                }
              />
            ))}
          </ul>
        </div>
      ))}
      <PartsToTake plan={pack.plan} />
      <p className="crew-note">Fetched {formatDateTime(pack.fetched_at)}</p>
    </section>
  );
}

function PrepLine({
  line,
  askAnswer,
  onTick,
}: {
  line: PackLine;
  askAnswer: boolean;
  onTick: (next: { done: boolean; answer: string | null }) => void;
}) {
  const [answer, setAnswer] = useState(line.answer ?? "");
  return (
    <li className={line.done ? "prep-done" : undefined}>
      <label>
        <input type="checkbox" checked={line.done} onChange={(e) => onTick({ done: e.target.checked, answer: answer || null })} />
        <span className="prep-text">{line.text}</span>
      </label>
      {line.detail ? <div className="crew-note prep-detail">{line.detail}</div> : null}
      {line.because ? <div className="crew-note prep-detail">Because: {line.because}</div> : null}
      <div className="crew-note prep-source">{line.source}</div>
      {askAnswer ? (
        <input
          className="input prep-answer"
          placeholder="Answer"
          aria-label={`Answer: ${line.text}`}
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          onBlur={() => {
            if ((answer || null) !== line.answer) {
              onTick({ done: line.done || answer !== "", answer: answer || null });
            }
          }}
        />
      ) : null}
    </li>
  );
}
